import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { offersPageRouteLoader } from '@/routes/offers/OffersPage.route'
import ODataStore from 'devextreme/data/odata/store'
import notify from 'devextreme/ui/notify'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLoaderData } from 'react-router-typesafe'
import {
  Column,
  ColumnChooser,
  DataGrid,
  Editing,
  Export,
  FilterRow,
  HeaderFilter,
  Item,
  Scrolling,
  SearchPanel,
  Selection,
  StateStoring,
  Toolbar,
  DataGridTypes,
  DataGridRef,
  Sorting,
  Search,
  FilterPanel,
  Pager,
  Button as GridButton,
} from 'devextreme-react/data-grid'
import { useScreenSize } from '@/themes/media-query'
import dxDataGrid, {
  CellPreparedEvent,
  ColumnButtonClickEvent,
  ExportingEvent,
  RowDblClickEvent,
} from 'devextreme/ui/data_grid'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import { ClickEvent } from 'devextreme/ui/button'
import { contatto_aziendale, entita_aziendale, offerta } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { confirm } from 'devextreme/ui/dialog'
import { EdmLiteral } from 'devextreme/data/odata/utils'
import { Accordion, Item as AccordionItem } from 'devextreme-react/accordion'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { GridCellColor } from '@/enums'
import { AxiosError, AxiosResponse } from 'axios'
import { StatoOfferta } from '@/routes/offers/OfferEditor.enums'
import { ContentReadyEvent } from 'devextreme/ui/accordion'
import {
  createDateFilterItemDescriptor,
  FormFilterItemDescriptorType,
} from '@/components/filter-form/GenericFilterForm.types'
import GenericFilterForm from '@/components/filter-form/GenericFilterForm'
import { useTokenRefresh, useAzureManager } from '@/auth/azure/azureManager'
import DateColumn from '@/components/date-column/DateColumn'
import { columnSourceFactory, isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import { gridTotalItemsLabel } from '@/constants'
import DocumentHead from '@/components/document-head/DocumentHead'
import { Providers } from '@microsoft/mgt-react'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'

const OffersPage = () => {
  const { title, aziende, userInfo, defaultCRUDAllowedRoles } = useLoaderData<typeof offersPageRouteLoader>()
  const token = useTokenRefresh()

  const { service, client } = useQsAdminApiManager()

  const { clientId } = useParams()
  const currentScreenSize = useScreenSize()
  const gridRef = useRef<DataGridRef>(null)
  const navigate = useNavigate()

  const getFiltersConfiguration = useCallback((): FormFilterItemDescriptorType[] => {
    const fltConf: FormFilterItemDescriptorType[] = [
      {
        fieldName: 'azienda',
        placeHolder: 'Azienda',
        valueKeyName: 'id',
        valueDisplayExpr: 'nome',
        values: aziende.data.value,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['sede/azienda/id', this.currentValue]]
        },
      },
      createDateFilterItemDescriptor('data_creazione', 'Data', 'Ultimo mese'),
    ]
    console.log('GENFLT - GETCONF :', fltConf)
    return fltConf
  }, [aziende])

  const initialFilterConfiguration = useMemo(() => getFiltersConfiguration(), [getFiltersConfiguration])
  const [filterConfiguration, setFilterConfiguration] = useState(initialFilterConfiguration)
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const getCurrentFilter = useCallback((): any[] => {
    let applied: boolean = false
    const currentFlts = []
    for (const flt of filterConfiguration) {
      const fltValue = flt.composeFilterItem()
      if (fltValue !== null) {
        currentFlts.push(fltValue)
      }
      if (!applied && flt.currentValue !== flt.defaultValue) applied = true
    }
    setFiltersApplied(applied)
    console.log('GENFLT - CURRENT:', currentFlts)
    return currentFlts
  }, [filterConfiguration])

  const [currentFilter, setCurrentFilter] = useState<any[]>(clientId ? [['sede/azienda/id', Number(clientId)]] : [])

  const offersDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/offerta`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: [
      'sede($expand=azienda($expand=agente($expand=commerciale_qs),rivenditore))',
      'autore',
      'stato',
      'riferimenti',
      'riferimenti_entita',
      'analisi',
      'commessa',
      'ordini',
    ],
    filter: currentFilter.length > 0 ? currentFilter : null,
    requireTotalCount: true,
  }

  const getGridHeight = useCallback(() => {
    let h = '79vh'
    if (currentScreenSize.isMedium) {
      h = '75vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '75vh'
    }
    return h
  }, [currentScreenSize])

  const onCellPrepared = (e: CellPreparedEvent) => {
    if (e.rowType === 'data' && e.column.dataField === 'stato.nome' && e.value) {
      switch (e.value) {
        case 'PERSA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`

          break
        }
        case 'APERTA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`

          break
        }
        case 'ORDINATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.LILAC}`

          break
        }
        case 'SUPERATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.ORANGE}`

          break
        }
      }
    }
  }

  const onExporting = (e: ExportingEvent) => {
    ExportDataGridToExcel(e.component)
  }

  const deleteOfferta = async (offerId: number) => {
    return await service.offerta(offerId).delete()
  }

  const onDeleteClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: offerta) => {
      const result = confirm(`<i>Vuoi davvero eliminare l'offerta <b>${rowdata.id}</b>?</i>`, 'Eliminazione offerta')
      result.then((dialogResult) => {
        if (dialogResult === false) return
        deleteOfferta(rowdata.id)
          .then(() => {
            // Aggiornamento griglia
            gridRef.current?.instance().refresh()

            notify(
              {
                message: `Offerta '${rowdata['ded_Dis']}' del cliente ${
                  rowdata['sede']?.azienda?.nome ?? 'Non definito'
                } eliminata con successo`,
                type: 'success',
                displayTime: 5000,
              },
              {
                position: 'bottom center',
                direction: 'up-push',
              },
            )
          })
          .catch((error: unknown) => {
            notify(
              {
                message: `Errore eliminazione offerta: ${error}`,
                type: 'error',
                displayTime: 5000,
              },
              {
                position: 'bottom center',
                direction: 'up-push',
              },
            )
          })
      })
    })
  }

  const handleNewRevisionClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: offerta) => {
      console.log('offerta', rowdata)
      gridRef?.current?.instance().beginCustomLoading('Creazione revisione in corso....')
      client
        .post('/api/offerta/createrevision', rowdata.id, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((response: AxiosResponse) => {
          console.log('response', response.data)
          navigate(`${response.data.id}`)
        })
        .catch((error: AxiosError) => {
          notify(`Errore creazione revisione. Dettagli : ${error}`, 'error', 2000)
        })
        .finally(() => {
          gridRef?.current?.instance().endCustomLoading()
        })
    })
  }

  const handleExportPDFClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: offerta) => {
      gridRef?.current?.instance().beginCustomLoading('Esportazione documento in corso....')
      console.log('offerta', rowdata)
      client
        .get('/api/offerta/exportpdf', {
          params: { offerId: Number(rowdata.id) },
          headers: {
            'Content-Disposition': `attachment;filename=${rowdata.mainDocumentUrl?.replace('.docx', '')}.pdf`,
            'Content-Type': 'application/octet-stream',
          },
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          console.log('response', response.data)
          saveAs(
            new Blob([response.data], { type: 'application/octet-stream' }),
            `${rowdata.mainDocumentUrl?.replace('.docx', '')}.pdf`,
          )
        })
        .catch((error: AxiosError) => {
          notify(`Errore esportazione documento. Dettagli : ${error}`, 'error', 2000)
        })
        .finally(() => {
          gridRef?.current?.instance().endCustomLoading()
        })
    })
  }

  const handleNewOrderClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: offerta) => {
      console.log('offerta', rowdata)
      if (rowdata.stato?.id === StatoOfferta.APERTA)
        navigate({
          pathname: '/orders/new',
          search: `?offerId=${rowdata?.id}`,
        })
      else notify(`L'offerta deve essere nello stato 'APERTA'. Stato: '${rowdata.stato?.nome}'`, 'warning', 3000)
    })
  }

  const handleOnClickSendOfferToReseller = async (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: offerta) => {
      console.log('offerta', rowdata)
      if (rowdata.sede?.azienda?.rivenditore) {
        gridRef?.current?.instance().beginCustomLoading('Invio email al rivenditore in corso....')
        client
          .post('/api/offerta/sendoffertoreseller', rowdata.id, {
            headers: {
              'Content-Type': 'application/json',
            },
          })
          .then((response: AxiosResponse) => {
            console.log('response', response.data)
            notify(`Offerta inviata correttamente al rivenditore.`, 'success', 2000)
          })
          .catch((error: AxiosError) => {
            notify(`Errore invio offerta al rivenditore. Dettagli : ${error}`, 'error', 2000)
          })
          .finally(() => {
            gridRef?.current?.instance().endCustomLoading()
          })
      } else
        notify(`L'offerta deve essere di un rivenditore. Cliente: '${rowdata.sede?.azienda?.nome}'`, 'warning', 3000)
    })
  }

  const handleDuplicateOfferClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(gridRef, (rowdata: offerta) => {
      console.log('offerta', rowdata)
      gridRef?.current?.instance().beginCustomLoading('Duplicazione offerta in corso....')
      client
        .post(
          '/api/offerta/duplicateoffer',
          { offerId: rowdata.id },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then((response: AxiosResponse) => {
          console.log('response', response.data)
          navigate(`${response.data.id}`)
        })
        .catch((error: AxiosError) => {
          notify(`Errore duplicazione offerta. Dettagli : ${error}`, 'error', 2000)
        })
        .finally(() => {
          gridRef.current?.instance().endCustomLoading()
        })
    })
  }

  const ExportDataGridToExcel = (component: dxDataGrid) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Offerte')
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Offers.xlsx')
      })
    })
  }

  const calculateRiferimentiValue = (rowData: offerta) => {
    let riferimenti: string = ''
    rowData.riferimenti?.map(
      (contatto: contatto_aziendale) => (riferimenti = `${riferimenti + contatto.fullname?.toUpperCase()} `),
    )
    rowData.riferimenti_entita?.map(
      (contatto: entita_aziendale) => (riferimenti = `${riferimenti + contatto.nome?.toUpperCase()} `),
    )
    return riferimenti.trim()
  }

  const cellRiferimentiRender = (cellData: DataGridTypes.ColumnCellTemplateData) => {
    return (
      <>
        {cellData.data.riferimenti?.map((contatto: contatto_aziendale, index: number) => {
          return (
            <div className={`dx-tag-content custom-tag-padding`} key={`ctc-${index}`}>
              <span>{contatto.fullname}</span>
            </div>
          )
        })}
        {cellData.data.riferimenti_entita?.map((entita: entita_aziendale, index: number) => {
          return (
            <div className={`dx-tag-content custom-tag-padding`} key={`ent-${index}`}>
              <span>{entita.nome}</span>
            </div>
          )
        })}
      </>
    )
  }

  const calculateRiferimentiFilterExpression = (
    filterValue: any,
    selectedFilterOperation: string | null,
    target: string,
  ): string | (() => any) | Array<any> => {
    console.log('FLT', filterValue, selectedFilterOperation, target)

    if (target === 'filterBuilder' || target === 'filterRow') {
      switch (selectedFilterOperation) {
        case '=': {
          return [
            [new EdmLiteral(`riferimenti/any(a: a/fullname eq '${filterValue}')`), '=', true],
            'or',
            [new EdmLiteral(`riferimenti_entita/any(a: a/nome eq '${filterValue}')`), '=', true],
          ]
        }
        case '<>': {
          return [
            [new EdmLiteral(`riferimenti/any(a: a/fullname eq '${filterValue}')`), '=', false],
            'and',
            [new EdmLiteral(`riferimenti_entita/any(a: a/nome eq '${filterValue}')`), '=', false],
          ]
        }
        case 'contains':
        case 'startswith':
        case 'endswith': {
          return [
            [new EdmLiteral(`riferimenti/any(a: ${selectedFilterOperation}(a/fullname, '${filterValue}'))`), '=', true],
            'or',
            [
              new EdmLiteral(`riferimenti_entita/any(a: ${selectedFilterOperation}(a/nome, '${filterValue}'))`),
              '=',
              true,
            ],
          ]
        }
        default: {
          // caso notcontains
          return [
            [new EdmLiteral(`riferimenti_entita/any(a: contains(a/nome, '${filterValue}'))`), '=', false],
            'and',
            [new EdmLiteral(`riferimenti/any(a: contains(a/fullname, '${filterValue}'))`), '=', false],
          ]
        }
      }
    } else if (target === 'search') {
      return [
        [new EdmLiteral(`riferimenti/any(a: contains(a/fullname, '${filterValue}'))`), '=', true],
        'or',
        [new EdmLiteral(`riferimenti_entita/any(a: contains(a/nome, '${filterValue}'))`), '=', true],
      ]
    }

    return filterValue
  }

  const calculateOrdineCellValue = (rowData: offerta) => {
    return rowData.ordini?.[0]?.ded_Dis
  }

  const calculateOrdineFilterExpression = (
    filterValue: any,
    selectedFilterOperation: string | null,
    target: string,
  ): string | (() => any) | Array<any> => {
    console.log('FLT', filterValue, selectedFilterOperation, target)

    if (target === 'filterBuilder' || target === 'filterRow') {
      switch (selectedFilterOperation) {
        case '=': {
          return [[new EdmLiteral(`ordini/any(a: a/ded_Dis eq '${filterValue}')`), '=', true]]
        }
        case '<>': {
          return [[new EdmLiteral(`ordini/any(a: a/ded_Dis eq '${filterValue}')`), '=', false]]
        }
        case 'contains':
        case 'startswith':
        case 'endswith': {
          return [[new EdmLiteral(`ordini/any(a: ${selectedFilterOperation}(a/ded_Dis, '${filterValue}'))`), '=', true]]
        }
        default: {
          // caso notcontains
          return [[new EdmLiteral(`ordini/any(a: contains(a/ded_Dis, '${filterValue}'))`), '=', false]]
        }
      }
    } else if (target === 'search' || target === 'headerFilter') {
      return [[new EdmLiteral(`ordini/any(a: contains(a/ded_Dis, '${filterValue}'))`), '=', true]]
    }

    return filterValue
  }

  useEffect(() => {
    getCurrentFilter()
  }, [])

  return (
    <>
      {!clientId && <h2 className={'content-block'}>{title}</h2>}
      <div className={clientId ? '' : 'content-block'}>
        {!clientId && (
          <div className="accordion-generic-filter-form">
            <Accordion
              className={filtersApplied ? 'with-filter' : ''}
              collapsible={true}
              onContentReady={(e: ContentReadyEvent<any, any>) => {
                e.component?.collapseItem(0)
              }}
            >
              <AccordionItem icon="filter" title={'Filtri'}>
                <GenericFilterForm
                  FormItemDescriptors={filterConfiguration}
                  resetFilterClicked={function (e: ClickEvent): void {
                    setFilterConfiguration(getFiltersConfiguration())
                  }}
                  filterValueChanged={function (): void {
                    const currentFlts = getCurrentFilter()
                    console.log('GENFLT - VALUE CHNG', currentFlts)
                    if (JSON.stringify(currentFlts) !== JSON.stringify(currentFilter)) {
                      console.log('GENFLT - SETTING FILT', currentFlts)
                      setCurrentFilter(currentFlts)
                    }
                  }}
                />
              </AccordionItem>
            </Accordion>
          </div>
        )}
        <DataGrid
          id={clientId ? 'client-offers-datagrid' : 'offers-datagrid'}
          height={getGridHeight()}
          dataSource={offersDataSource}
          noDataText="Nessuna offerta trovata"
          className={'dx-card wide-card'}
          showBorders={false}
          showColumnLines={true}
          wordWrapEnabled={false}
          showRowLines={true}
          focusedRowEnabled={true}
          rowAlternationEnabled={true}
          allowColumnResizing={true}
          allowColumnReordering={true}
          width="100%"
          onCellPrepared={onCellPrepared}
          onExporting={onExporting}
          ref={gridRef}
          columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
          remoteOperations={{
            filtering: true,
            grouping: false,
            groupPaging: false,
            paging: true,
            sorting: true,
            summary: true,
          }}
          grouping={{
            // Fondamentale per il raggruppamento veloce!!!
            autoExpandAll: true,
          }}
          groupPanel={{
            visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
            emptyPanelText: 'Trascina qui una colonna per raggruppare',
          }}
          onRowDblClick={(e: RowDblClickEvent<any, any>) => {
            if (e.rowType === 'data') navigate(`${e.data.id}`)
          }}
        >
          <Pager
            visible={true}
            allowedPageSizes={'auto'}
            displayMode={'compact'}
            showPageSizeSelector={false}
            showInfo={true}
            showNavigationButtons={false}
            infoText={gridTotalItemsLabel}
          />
          <FilterRow visible={true} />
          <Sorting mode="multiple"></Sorting>
          <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 200 : 250} />
          <Export enabled={true} />
          <Editing allowAdding={true} />
          <FilterPanel visible={true} />
          <HeaderFilter visible={true} />
          <Selection mode="single" />
          <ColumnChooser enabled={true} />
          <Scrolling mode="virtual" />
          <StateStoring
            enabled={true}
            type="localStorage"
            storageKey={clientId ? 'client-offers-datagrid' : 'offers-datagrid'}
            savingTimeout={50}
          />
          <Column
            visible={false}
            type="buttons"
            width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
            alignment="left"
            showInColumnChooser={false}
          >
            <GridButton
              hint="Details"
              icon="search"
              onClick={(e: ColumnButtonClickEvent) => {
                navigate(`${e.row?.data.id}`)
              }}
            ></GridButton>
          </Column>
          <Column
            dataField={'id'}
            caption="ID"
            visible={false}
            dataType="number"
            defaultSortOrder="desc"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
            }
          >
            {!clientId && <HeaderFilter dataSource={columnSourceFactory(token, 'offerta', 'id', true)}></HeaderFilter>}
          </Column>
          <Column
            dataField="ded_Dis"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: offerta = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`${data.id}`}
                  text={data.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
                ></ObjectCodeLink>
              )
            }}
            visible={true}
            caption="CODICE"
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 110 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 110 : undefined}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 15 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'offerta', 'ded_Dis', true)}>
                <Search enabled={true} searchExpr={'ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="revisione"
            visible={true}
            caption="REVISIONE"
            width={'3%'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
            }
          >
            {!clientId && <HeaderFilter dataSource={columnSourceFactory(token, 'offerta', 'revisione')}></HeaderFilter>}
          </Column>
          <Column
            dataField="sede.azienda.nome"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: offerta = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/clients/${data.sede?.azienda?.id}`}
                  text={data.sede?.azienda?.nome}
                  disabled={clientId ? true : !isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
                ></ObjectCodeLink>
              )
            }}
            caption="AZIENDA"
            visible={clientId === undefined}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 16 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'azienda', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="sede.nome"
            caption="SEDE"
            allowHeaderFiltering={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
            }
          />
          <Column
            dataField="sede.azienda.agente.commerciale_qs.fullname"
            caption="AGENTE"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'agente', 'commerciale_qs.fullname')}>
                <Search enabled={true} searchExpr={'commerciale_qs.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          <Column dataField={'sede.azienda.rivenditore.nome'} caption={'RIVENDITORE'} visible={false}>
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'Azienda', 'rivenditore.nome')}>
                <Search enabled={true} searchExpr={'rivenditore.nome'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="autore.fullname"
            caption="AUTORE"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'offerta', 'autore.fullname')}>
                <Search enabled={true} searchExpr={'autore.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            name={'info_riferimenti'}
            caption={'RIFERIMENTI'}
            calculateFilterExpression={calculateRiferimentiFilterExpression}
            calculateCellValue={calculateRiferimentiValue}
            cellRender={cellRiferimentiRender}
            allowFiltering={true}
            allowHeaderFiltering={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 10 : undefined
            }
          />
          {DateColumn({
            dataField: 'data_creazione',
            caption: 'DATA',
            format: 'dd/MM/yyyy',
            width: 100,
            hidingPriority:
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 14 : undefined,
          })}
          <Column
            dataField="note"
            caption="NOTE"
            allowHeaderFiltering={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 13 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 150 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 150 : undefined}
          />
          <Column
            dataField="stato.nome"
            caption="STATO"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 11 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'offerta', 'stato.nome')}>
                <Search enabled={true} searchExpr={'stato.nome'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            name="info_ordine"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: offerta = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/orders/${data.ordini?.[0]?.id}`}
                  text={data.ordini?.[0]?.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [...defaultCRUDAllowedRoles, Roles.Marketing])}
                ></ObjectCodeLink>
              )
            }}
            caption="ORDINE"
            calculateCellValue={calculateOrdineCellValue}
            calculateFilterExpression={calculateOrdineFilterExpression}
            allowFiltering={true}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'ordine', 'ded_Dis', true)}>
                <Search enabled={true} searchExpr={'ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="analisi.ded_Dis"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: offerta = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/analysis/${data.analisi?.id}`}
                  text={data.analisi?.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [...defaultCRUDAllowedRoles, Roles.Marketing])}
                ></ObjectCodeLink>
              )
            }}
            caption="ANALISI"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'offerta', 'analisi.ded_Dis', true)}>
                <Search enabled={true} searchExpr={'analisi.ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="commessa.ded_Dis"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: offerta = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/projects/${data.commessa?.id}`}
                  text={data.commessa?.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [...defaultCRUDAllowedRoles, Roles.Marketing])}
                ></ObjectCodeLink>
              )
            }}
            caption="COMMESSA"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'offerta', 'commessa.ded_Dis', true)}>
                <Search enabled={true} searchExpr={'commessa.ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField="attivitaTecnicaRichiesta"
            caption="ATTIVITA' RICHIESTA"
            allowHeaderFiltering={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
            }
          ></Column>
          <Column
            dataField="rinnovo"
            caption="RINNOVO"
            allowHeaderFiltering={false}
            visible={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
            }
          ></Column>
          <Toolbar>
            <Item
              location="before"
              locateInMenu="never"
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Refresh',
                text: 'Refresh',
                icon: 'refresh',
                onClick: (e: ClickEvent) => {
                  gridRef.current?.instance().refresh()
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item name="groupPanel" />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Supervisor,
                Roles.Sales,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              name="addRowButton"
              options={{
                onClick: () => {
                  navigate(`new`)
                },
                hint: 'Nuova offerta',
                text: 'Nuova offerta',
              }}
            />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.Supervisor,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Nuova rev',
                text: 'Nuova rev',
                icon: 'newfolder',
                onClick: handleNewRevisionClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.Supervisor,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Duplica',
                text: 'Duplica',
                icon: 'copy',
                onClick: handleDuplicateOfferClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.ExternalSales,
                Roles.Supervisor,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Esporta documento .PDF',
                text: 'Esporta documento .PDF',
                icon: 'pdffile',
                onClick: handleExportPDFClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Supervisor,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Crea ordine',
                text: 'Crea ordine',
                icon: 'ordersbox',
                onClick: handleNewOrderClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Invia Offerta Rivenditore',
                text: 'Invia Offerta Rivenditore',
                icon: 'email',
                onClick: handleOnClickSendOfferToReseller,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.ExternalSales,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Apri listino Qs Informatica',
                text: 'Apri listino Qs Informatica',
                icon: 'money',
                onClick: async (e: ClickEvent) => {
                  const priceListPath = '_Templates/QsPlatformPriceList.xlsx'
                  try {
                    gridRef?.current?.instance().beginCustomLoading('Apertura listino in corso....')
                    const response = await Providers.globalProvider.graph
                      .api(`drives/${import.meta.env.VITE_QSADMINSITE_DRIVE_ID}/root:/${priceListPath}`)
                      .get()
                    window.open(response.webUrl, '_blank')
                  } catch (error) {
                    notify(`Errore individuazione listino prezzi. Dettagli : ${error}`, 'error', 4000)
                  } finally {
                    gridRef?.current?.instance().endCustomLoading()
                  }
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Elimina offerta',
                text: 'Elimina offerta',
                icon: 'trash',
                onClick: onDeleteClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              name="columnChooserButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
            />
            <Item
              name="exportButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
            />
            <Item name="searchPanel" />
          </Toolbar>
        </DataGrid>
      </div>
    </>
  )
}

export default OffersPage
